import { Send, Bot, User, Loader2 } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function ChatPanel() {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hello! I am Orion, your AI coding assistant. Ask me anything about your code.',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function send() {
    if (!input.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/ai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: 'ollama',
          model: 'qwen3:latest',
          messages: [...messages, userMsg].map((m) => ({ role: m.role, content: m.content })),
          max_tokens: 4096,
        }),
      });
      const data = await res.json();
      const reply = data.content || data.choices?.[0]?.message?.content || 'No response';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${(e as Error).message}` },
      ]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'flex-start' }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: msg.role === 'assistant' ? '#1f2937' : '#0d1117',
                border: '1px solid #30363d',
                flexShrink: 0,
              }}
            >
              {msg.role === 'assistant' ? (
                <Bot size={14} color="#58a6ff" />
              ) : (
                <User size={14} color="#c9d1d9" />
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                color: '#c9d1d9',
                minWidth: 0,
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div style={{ padding: '12px 16px', borderTop: '1px solid #30363d', background: '#161b22' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask Orion..."
            style={{
              flex: 1,
              padding: '10px 14px',
              borderRadius: 6,
              border: '1px solid #30363d',
              background: '#0d1117',
              color: '#c9d1d9',
              fontSize: 13,
              outline: 'none',
            }}
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            style={{
              padding: '10px 16px',
              borderRadius: 6,
              border: 'none',
              background: loading ? '#21262d' : '#58a6ff',
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              opacity: loading || !input.trim() ? 0.5 : 1,
            }}
          >
            {loading ? <Loader2 size={16} /> : <Send size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
}
